import type { DropDownItem } from '@/components/common/DropDown.vue';
import { computed, reactive, watch } from 'vue';
import type {
  Account,
  DebitNormal,
  DebitNormSaveEntry,
  DebitNormsSaveCallbacks,
  DebitNormsWithOrder,
} from '../types';
import { createDebitNormsStore } from './createDebitNormsStore';

type Mode = 'read-only' | 'edit';

export interface UseDebitNormsLogicProps {
  accounts: Account[];
  debitNorms: DebitNormsWithOrder[];
  mode: Mode;
}

export interface DebitNormsState {
  debitNorms: DebitNormal[];
  isEditing: boolean;
  isSaving: boolean;
  mode: Mode;
  newEntry: { accountNumber: string; rate: string };
  accounts: Account[];
  accountItems: DropDownItem[];
}

export interface DebitNormsAction {
  startEdit: () => void;
  requestSave: () => void;
  cancel: () => void;
  addEntry: () => void;
  deleteEntry: (id: string) => void;
  updateEntry: (id: string, value: string) => void;
  updateNewEntry: (field: 'accountNumber' | 'rate', value: string) => void;
}

export const useDebitNormsLogic = (
  props: UseDebitNormsLogicProps,
  emit: (
    event: 'save',
    entries: DebitNormSaveEntry[],
    callbacks: DebitNormsSaveCallbacks,
  ) => void,
): { state: DebitNormsState; action: DebitNormsAction } => {
  const parseRate = (value: string): number =>
    Math.round(parseFloat(value.replace(/[\s  ]/g, '').replace(',', '.')) || 0);

  const store = createDebitNormsStore(
    props.debitNorms.map((e) => ({
      ...e,
      isNew: false,
      account: { ...e.account },
    })),
  );

  watch(
    () => props.debitNorms,
    (newData) => {
      if (store.status.value !== 'editing') {
        store.dispatch({
          type: 'reset',
          entries: newData.map((e) => ({
            ...e,
            isNew: false,
            account: { ...e.account },
          })),
        });
      }
    },
  );

  const action: DebitNormsAction = {
    startEdit: () => {
      store.dispatch({ type: 'start-edit' });
    },
    requestSave: () => {
      const savedEntries = store.dispatch({ type: 'request-save' });
      if (savedEntries) {
        emit(
          'save',
          savedEntries.map((e): DebitNormSaveEntry => {
            if (e.isNew) {
              return {
                isNew: true,
                order: e.order,
                account: { ...e.account },
                rate: e.rate,
              };
            }
            return {
              isNew: false,
              id: e.id,
              order: e.order,
              account: { ...e.account },
              rate: e.rate,
            };
          }),
          {
            onSuccess: () => store.dispatch({ type: 'complete-save' }),
            onError: () => store.dispatch({ type: 'revert-save' }),
          },
        );
      }
    },
    cancel: () => {
      store.dispatch({ type: 'cancel' });
    },
    addEntry: () => {
      const accountNumber = parseInt(
        store.newEntry.accountNumber.replace(/\D/g, ''),
        10,
      );
      const rate = parseRate(store.newEntry.rate);
      const account = props.accounts.find((a) => a.number === accountNumber);
      if (!accountNumber || !account) return;
      store.dispatch({
        type: 'add-entry',
        entry: {
          id: String(Date.now()),
          isNew: true,
          order: store.entries.length,
          account: { number: account.number, name: account.name },
          rate,
        },
      });
    },
    deleteEntry: (id: string) => {
      store.dispatch({ type: 'delete-entry', id });
    },
    updateEntry: (id: string, value: string) => {
      store.dispatch({ type: 'update-entry', id, rate: parseRate(value) });
    },
    updateNewEntry: (field: 'accountNumber' | 'rate', value: string) => {
      store.dispatch({ type: 'update-new-entry', field, value });
    },
  };

  return {
    state: reactive({
      get debitNorms(): DebitNormal[] {
        return (
          [...store.entries]
            .sort((a, b) => a.order - b.order)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .map(({ order: _order, isNew: _isNew, ...rest }) => rest)
        );
      },
      isEditing: computed(() => store.status.value === 'editing'),
      isSaving: computed(() => store.status.value === 'saving'),
      mode: computed(() => props.mode),
      get newEntry() {
        return store.newEntry;
      },
      get accounts() {
        return props.accounts;
      },
      get accountItems(): DropDownItem[] {
        return props.accounts
          .filter(
            (a: Account) =>
              !store.entries.some((dn) => dn.account.number === a.number),
          )
          .map((a: Account) => ({
            value: String(a.number),
            text: `${a.number} – ${a.name}`,
          }));
      },
    }),
    action,
  };
};
